import React from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export const ProtectedLayout = () => {
  const { session, profile, loading, signOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50/50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profile && !profile.onboarding_completed && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/onboarding" replace />;
  }

  if (profile && profile.onboarding_completed && location.pathname === '/app/onboarding') {
    return <Navigate to="/app/dashboard" replace />;
  }

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: FiIcons.FiHome },
    { name: 'Cards', href: '/app/cards', icon: FiIcons.FiCreditCard },
    { name: 'Events', href: '/app/events', icon: FiIcons.FiCalendar },
    { name: 'Connections', href: '/app/connections', icon: FiIcons.FiUsers },
    { name: 'Contacts', href: '/app/contacts', icon: FiIcons.FiBookOpen },
    { name: 'Moments', href: '/app/moments', icon: FiIcons.FiFeather },
    { name: 'Follow-ups', href: '/app/reminders', icon: FiIcons.FiClock },
    { name: 'Analytics', href: '/app/analytics', icon: FiIcons.FiBarChart2 },
    { name: 'Profile', href: '/app/profile', icon: FiIcons.FiUser },
    { name: 'Settings', href: '/app/settings', icon: FiIcons.FiSettings },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Sidebar - Cleaned up spacing, refined active states (Linear style) */}
      <div className="hidden md:flex flex-col w-[260px] bg-white border-r border-slate-200/60">
        <div className="h-16 flex items-center px-6 border-b border-slate-100">
          <Link to="/app/dashboard" className="flex items-center space-x-2.5">
            <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center shadow-sm">
              <SafeIcon icon={FiIcons.FiLayers} className="text-white text-sm" />
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">Meetoi</span>
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto py-6">
          <nav className="px-4 space-y-1">
            {navigation.map((item) => {
              const isActive = item.href === '/app/dashboard' 
                ? location.pathname === item.href 
                : location.pathname.startsWith(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 group
                    ${isActive 
                      ? 'bg-slate-900/5 text-slate-900' 
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <SafeIcon 
                    icon={item.icon} 
                    className={`flex-shrink-0 mr-3 h-4 w-4 transition-colors
                      ${isActive ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'}`} 
                  />
                  <span className="truncate tracking-tight">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center px-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-xs ring-1 ring-slate-200/50">
              {profile?.full_name?.charAt(0) || session.user.email.charAt(0)}
            </div>
            <div className="ml-3 truncate">
              <p className="text-sm font-bold text-slate-900 truncate tracking-tight">
                {profile?.full_name || 'User'}
              </p>
              <p className="text-xs text-slate-500 truncate">{session.user.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-500 hover:text-slate-900 rounded-xl" onClick={signOut}>
            <SafeIcon icon={FiIcons.FiLogOut} className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <div className="md:hidden h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-4 sticky top-0 z-30">
          <Link to="/app/dashboard" className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiLayers} className="text-white text-sm" />
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">Meetoi</span>
          </Link>
          <div className="flex items-center">
            <Link to="/app/contacts" className="p-2 rounded-xl text-slate-500 hover:bg-slate-100" title="Contacts">
              <SafeIcon icon={FiIcons.FiBookOpen} className="h-4 w-4" />
            </Link>
            <Link to="/app/moments" className="p-2 rounded-xl text-slate-500 hover:bg-slate-100" title="Moments">
              <SafeIcon icon={FiIcons.FiFeather} className="h-4 w-4" />
            </Link>
            <Button variant="ghost" size="sm" onClick={signOut} className="rounded-xl">
              <SafeIcon icon={FiIcons.FiLogOut} className="h-4 w-4 text-slate-500" />
            </Button>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-8 px-4 sm:px-6 md:px-8 max-w-5xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};